/**
 * Своя правка ВРЕМЕНИ реплики эфира держит место на дорожке озвучки, пока не вышла в эфир.
 *
 * Проба 20261105-P50m20g, эфир cfc28f08, папка 3 en, E7: переводчик перетащил озвучку реплики лектора 042015
 * «It’s all very individual.» на 180 мс позже (62740 → 62920, 08:52:04.8). Правка ждала круга (в той же папке шёл круг
 * замены ИИ), и пока она ждала (08:52:05–08:53:18), блок на дорожке прыгал между новым и прежним местом при каждом
 * обновлении с сервера: 62920 → 62740 (перечитывание папки до того, как легло сохранение) → 62920 (сервер записал
 * место) → 62740 (выкладка круга замены ИИ переписала папку прежним местом) → 62920 (круг правки, в эфире 08:53:21).
 * Итог верный, но переводчик мог решить, что перетаскивание откатилось.
 *
 * Теперь: пока своя правка места не в эфире, дорожка держит место из правки — с пометкой «ждёт круга»
 * (`timing_pending` у реплики). Держание снимается, когда:
 *  - заявка этой правки закончилась (`settle` по заявкам эфира: в эфире — на сервере уже новое место; вопрос, отказ,
 *    упал — прежнее, и следующее обновление с сервера покажет его);
 *  - сервер поставил реплику на третье место — ни прежнее, ни из правки (её поставил круг иначе или правил кто-то ещё);
 *  - реплики больше нет (фраза разложена заново) или прошло `ttlMs` (5 мин — круг с ожиданием прохода папки длится
 *    минуты);
 *  - сохранение правки отказано (`drop`).
 * Пока сервер отдаёт прежнее место или уже место из правки — на дорожке место из правки. Чужие и машинные сдвиги не
 * держатся: только то, что перетащил этот редактор.
 */

// Папки и реплики — в том виде, в каком их отдаёт сервер (как и прежде, без строгого типа).
type Row = any;
type Folder = any;

/** Заявка правки эфира, как её отдаёт прогресс эфира (`/progress`, StreamProgress): только то, что нужно здесь. */
export interface TimingFixLike {
    id?: string;
    state?: string;
    folder?: number | string;
    lang?: string;
    requested_at?: string;
    timing?: Record<string, { to?: number | null } | null | undefined> | null;
}

export interface SavedTimingPins {
    /**
     * Данные папок языка легли с сервера: запомнить место сервера по репликам и вернуть папки, где у держащихся
     * реплик — место из правки и пометка `timing_pending`. Неизменённые папки и реплики — те же объекты.
     */
    land: (lang: string, folders: Folder[]) => Folder[];
    /**
     * Человек отпустил реплику на новом месте (правка времени эфира): держать его. `fallbackBefore` — место реплики в
     * данных редактора, если сервер её ещё не присылал.
     */
    pin: (lang: string, folderNum: number, id: string, start: number, fallbackBefore?: number | null) => void;
    /** Сохранение правки отказано — держать нечего. -> была ли держащейся. */
    drop: (lang: string, folderNum: number, id: string) => boolean;
    /** Заявки эфира: правка, чья заявка закончилась, — не держится. -> ключи снятых (`lang:folder:id`). */
    settle: (fixes: TimingFixLike[]) => string[];
    /** Держится ли реплика сейчас. */
    held: (lang: string, folderNum: number, id: string) => boolean;
    size: () => number;
}

export const SAVED_TIMING_PIN_TTL_MS = 5 * 60 * 1000;
/** Место «то же» — в пределах этого, мс (сервер пишет место округлённым). */
export const TIMING_PIN_EPS_MS = 1.5;
/** Заявка старше держания больше чем на это — не про эту правку, мс. */
const FIX_BEFORE_PIN_MS = 60 * 1000;
/** Конечные состояния заявки правки (site_server `stream_edits`, StreamProgress `FINAL_FIX_STATES`). */
const FINAL_STATES = ["fixed", "failed", "nothing_to_fix", "refused", "needs_translator"];

const rowId = (row: Row): string => String(row?.id ?? row?.original_id ?? "");
export const timingPinKey = (lang: string, folderNum: number, id: string) => `${lang}:${folderNum}:${id}`;
const num = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? value : null;
const near = (a: number | null, b: number | null): boolean => a !== null && b !== null && Math.abs(a - b) < TIMING_PIN_EPS_MS;

interface Pin {
    lang: string;
    folderNum: number;
    id: string;
    start: number;
    before: number | null;
    at: number;
    until: number;
}

export function createSavedTimingPins(opts: { now?: () => number; ttlMs?: number } = {}): SavedTimingPins {
    const now = opts.now || (() => Date.now());
    const ttlMs = opts.ttlMs ?? SAVED_TIMING_PIN_TTL_MS;
    // Место сервера по репликам — каким оно было в последних данных с сервера (до держания).
    const server = new Map<string, number>();
    const pins = new Map<string, Pin>();

    return {
        land: (lang, folders) => {
            if (!Array.isArray(folders)) return folders;
            const t = now();
            return folders.map((folder: Folder, index: number) => {
                const folderNum = index + 1;
                const rows: Row[] = Array.isArray(folder?.translations) ? folder.translations : null;
                if (!rows) return folder;
                const seen = new Set<string>();
                let changed = false;
                const next = rows.map((row: Row) => {
                    const id = rowId(row);
                    if (!id) return row;
                    const key = timingPinKey(lang, folderNum, id);
                    seen.add(key);
                    const there = num(row?.start);
                    if (there !== null) server.set(key, there);
                    const pin = pins.get(key);
                    if (!pin) return row;
                    if (t > pin.until || there === null || (!near(there, pin.start) && !near(there, pin.before))) {
                        // Держать поздно, или реплику поставили на третье место (круг иначе, кто-то ещё) — как сервер.
                        pins.delete(key);
                        return row;
                    }
                    changed = true;
                    return { ...row, start: pin.start, timing_pending: true };
                });
                pins.forEach((pin, key) => {
                    if (pin.lang === lang && pin.folderNum === folderNum && !seen.has(key)) pins.delete(key);
                });
                return changed ? { ...folder, translations: next } : folder;
            });
        },
        pin: (lang, folderNum, id, start, fallbackBefore) => {
            const key = timingPinKey(lang, folderNum, String(id));
            const before = server.has(key) ? (server.get(key) as number) : num(fallbackBefore);
            if (!Number.isFinite(start) || near(before, start)) {
                pins.delete(key);
                return;
            }
            const t = now();
            pins.set(key, { lang, folderNum, id: String(id), start, before, at: t, until: t + ttlMs });
        },
        drop: (lang, folderNum, id) => pins.delete(timingPinKey(lang, folderNum, String(id))),
        settle: (fixes) => {
            const released: string[] = [];
            if (!Array.isArray(fixes) || !pins.size) return released;
            pins.forEach((pin, key) => {
                const done = fixes.some((fix) => {
                    if (!fix || !FINAL_STATES.includes(String(fix.state || ""))) return false;
                    if (fix.folder !== undefined && Number(fix.folder) !== pin.folderNum) return false;
                    if (fix.lang && String(fix.lang) !== pin.lang) return false;
                    const to = num(fix.timing?.[pin.id]?.to);
                    if (to === null || !near(to, Math.round(pin.start))) return false;
                    const asked = Date.parse(String(fix.requested_at || ""));
                    return !Number.isFinite(asked) || asked >= pin.at - FIX_BEFORE_PIN_MS;
                });
                if (done) {
                    pins.delete(key);
                    released.push(key);
                }
            });
            return released;
        },
        held: (lang, folderNum, id) => pins.has(timingPinKey(lang, folderNum, String(id))),
        size: () => pins.size,
    };
}

/** Снять пометку «ждёт круга» у реплик `keys` (держание снято): место на дорожке — какое есть, до данных сервера. */
export function clearTimingPending(folders: Folder[], lang: string, keys: ReadonlySet<string>): Folder[] {
    if (!Array.isArray(folders) || !keys.size) return folders;
    return folders.map((folder: Folder, index: number) => {
        const rows: Row[] = Array.isArray(folder?.translations) ? folder.translations : null;
        if (!rows) return folder;
        let changed = false;
        const next = rows.map((row: Row) => {
            if (!row?.timing_pending || !keys.has(timingPinKey(lang, index + 1, rowId(row)))) return row;
            changed = true;
            const rest = { ...row };
            delete rest.timing_pending;
            return rest;
        });
        return changed ? { ...folder, translations: next } : folder;
    });
}

/** `clearTimingPending` по всем языкам данных редактора (`subtitlesDataAll`): тот же объект, если снимать нечего. */
export function clearTimingPendingAll<T extends Record<string, any>>(data: T, keys: ReadonlySet<string>): T {
    if (!keys.size) return data;
    let next = data;
    for (const rowLang of Object.keys(data)) {
        const folders = data[rowLang];
        const cleared = clearTimingPending(folders, rowLang, keys);
        if (cleared !== folders) next = { ...next, [rowLang]: cleared };
    }
    return next;
}
